import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../Components/Button";
import Select from "react-select";
import {
  clearAccountingAccount,
  clearGetBusiness,
  requestCreateAccountingAccount,
  requestDocumentExcelAccUpload,
  requestGetBusiness,
  requestListBusiness,
  useAccountingDispatch,
  useAccountingState,
  useAuthState,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
} from "../../Context";
import { useLocation, useNavigate } from "react-router-dom";
import { Alert } from "../../Components/Alert";
import InputSkeleton from "../../Components/InputSkeleton";
import { ModalConfirm } from "../../Components/ModalConfirm";
import { ACCOUNT_CATEGORY_OPTIONS, ACCOUNT_CODE } from "../../Constants";
import {
  classNames,
  getAccountingFileName,
  getErrorMessages,
  getSafeInternalPath,
} from "../../Utils";
import FormLabel from "../../Components/FormLabel";
import { Modal } from "../../Components/Modal";
import { DocumentIcon } from "@heroicons/react/outline";
import BusinessStatusBanner from "../../Components/v2/Business/BusinessStatusBanner";
import { isBusinessWriteBlocked } from "../../Constants/business";
import EmptyBusiness from "../../Components/EmptyBusiness";
import { downloadData, getUrl } from "aws-amplify/storage";

export const AccountCreate = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [selectedBusiness, setSelectedBusiness] = useState();
  const [code, setcode] = useState("");
  const [accountName, setAccountName] = useState("");
  const [category, setCategory] = useState("");
  const [fieldError, setfieldError] = useState(false);
  const [showSuccessModal, setshowSuccessModal] = useState(false);
  const [initialCode, setInitialCode] = useState("110");
  const [fileError, setfileError] = useState(null);
  const [imported, setimported] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [fieldErrorFile, setfieldErrorFile] = useState({
    status: false,
    message: "",
  });
  const [selectedFile, setselectedFile] = useState("");

  const [businessError, setbusinessError] = useState(null);
  const [codeError, setcodeError] = useState(null);
  const [nameError, setnameError] = useState(null);
  const [categoryError, setcategoryError] = useState(null);
  const [submitted, setsubmitted] = useState(false);

  const [downloading, setdownloading] = useState(false);
  const [downloadProgress, setdownloadProgress] = useState(0);
  const [downloadDocError, setdownloadDocError] = useState(null);
  const [showDownloadErrorModal, setshowDownloadErrorModal] = useState(false);
  const downloadRef = useRef(null);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const businessIdParam = searchParams.get("id");
  // A screen that hits a missing account can send the user here with the account
  // already described, and be returned to once it exists. Saldo Awal needs
  // account 330999 before an unbalanced total can be parked anywhere.
  const nameParam = searchParams.get("name");
  const codeParam = searchParams.get("code");
  const categoryParam = searchParams.get("category");
  const returnToParam = searchParams.get("returnTo");

  const dispatch = useAccountingDispatch();
  const {
    submitLoading,
    submitError,
    inputError,
    accountID,
    uploadError,
    progressUpload,
    storageRequest,
    uploadLoading,
    uploadSuccess,
    uploadStatusError,
  } = useAccountingState();

  const businessDispatch = useBusinessTransactionDispatch();
  const {
    businesses,
    business,
    businessError: getBusinessError,
    loading: businessLoading,
  } = useBusinessTransactionState();
  const { user } = useAuthState();

  const accountCategories = useMemo(() => {
    return ACCOUNT_CATEGORY_OPTIONS.map((item) => ({
      ...item,
      label: t(item.label),
    }));
  }, [t]);

  useEffect(() => {
    if (user.id) {
      requestListBusiness(businessDispatch, { owner: user.id });
    }
  }, [businessDispatch, user.id]);

  useEffect(() => {
    if (businessIdParam) {
      requestGetBusiness(businessDispatch, { id: businessIdParam });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessIdParam]);

  // Prefill from the query string. Runs once: the fields stay editable, so
  // re-applying them would fight the user.
  useEffect(() => {
    if (nameParam) setAccountName(nameParam);
    if (codeParam) {
      setInitialCode(codeParam.slice(0, 3));
      setcode(codeParam.slice(3));
    }
    if (categoryParam) {
      const matched = accountCategories.find(
        (item) => item.value === categoryParam,
      );
      if (matched) setCategory(matched);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (business.id) {
      setSelectedBusiness({
        ...business,
        label: business.name,
        value: business.id,
      });
    }
  }, [business]);

  useEffect(() => {
    if (selectedBusiness?.value) {
      clearGetBusiness(businessDispatch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBusiness]);

  useEffect(() => {
    if (accountID) {
      setshowSuccessModal(true);
    }
  }, [accountID]);

  useEffect(() => {
    if (inputError) {
      if (inputError.name) {
        setnameError(inputError.name);
      } else {
        setnameError("");
      }

      if (inputError.code) {
        setcodeError(inputError.code);
      } else {
        setcodeError("");
      }
    } else {
      setnameError("");
      setcodeError("");
    }
  }, [inputError]);

  useEffect(() => {
    if (submitted) {
      const isBusinessValid = Boolean(selectedBusiness);
      const isCodeValid = code.length === 3;
      const isNameValid = Boolean(accountName);
      const isCategoryValid = Boolean(category);

      if (isBusinessValid) {
        setErrorMessage("business", "");
      } else {
        setErrorMessage("business", "Business is required.");
      }

      if (isCodeValid) {
        setErrorMessage("code", "");
      } else {
        if (code.length === 0) {
          setErrorMessage("code", "Code is required.");
        }
        if (code.length > 0 && code.length < 3) {
          setErrorMessage("code", "Code must be exactly 6 characters long.");
        }
      }

      if (isNameValid) {
        setErrorMessage("name", "");
      } else {
        setErrorMessage("name", "Account name is required.");
      }

      if (isCategoryValid) {
        setErrorMessage("category", "");
      } else {
        setErrorMessage("category", "Category is required.");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, accountName, category, submitted]);

  useEffect(() => {
    if (imported && selectedFile) {
      if (selectedFile) {
        setErrorMessage("file", "");
      } else {
        setErrorMessage("file", "Select a file before clicking Upload.");
      }
    }
  }, [selectedFile, imported]);

  useEffect(() => {
    if (uploadSuccess) {
      setShowProgressModal(false);
      setshowSuccessModal(true);
    } else if (uploadError) {
      setShowErrorModal(true);
      setShowProgressModal(false);
    }
  }, [uploadSuccess, uploadError]);

  const validateInput = () => {
    let currentValidBusiness = true;
    let currentValidCode = true;
    let currentValidName = true;
    let currentValidCategory = true;

    const isBusinessValid = Boolean(selectedBusiness);
    const isCodeValid = code.length === 3;
    const isNameValid = Boolean(accountName);
    const isCategoryValid = Boolean(category);

    currentValidBusiness = isBusinessValid;
    currentValidCode = isCodeValid;
    currentValidName = isNameValid;
    currentValidCategory = isCategoryValid;

    return (
      currentValidBusiness &&
      currentValidCode &&
      currentValidName &&
      currentValidCategory
    );
  };

  const setErrorMessage = (field, message) => {
    switch (field) {
      case "business":
        setbusinessError(message);
        break;
      case "code":
        setcodeError(message);
        break;
      case "name":
        setnameError(message);
        break;
      case "category":
        setcategoryError(message);
        break;
      case "file":
        setfileError(message);
        break;
      default:
        break;
    }
  };

  const onSave = () => {
    setsubmitted(true);
    const valid = validateInput();

    if (valid) {
      setfieldError(false);
      const combinedCode = initialCode + code;

      const input = {
        business_id: selectedBusiness.value,
        name: accountName,
        code: combinedCode,
        category: category.value,
      };

      requestCreateAccountingAccount(dispatch, { input });
    } else {
      setfieldError(true);
      return;
    }
  };

  const navigateToAccount = () => {
    setshowSuccessModal(false);
    if (!submitError || !uploadError) {
      if (accountID) {
        // A caller that sent us here to create a specific account gets the user
        // back, with the account it was waiting for now in the list.
        navigate(
          getSafeInternalPath(
            returnToParam,
            `/account?id=${selectedBusiness.value}`,
          ),
        );
        clearAccountingAccount(dispatch);
      } else {
        navigate(`/account/import?id=${selectedBusiness.id}`);
      }
    }
  };

  const handleFileUpload = (event) => {
    event.preventDefault();
    const file = event.dataTransfer
      ? event.dataTransfer.files[0]
      : event.target.files[0];
    const maxSize = 100 * 1024; // 100 KB

    if (
      file &&
      file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" &&
      file.size <= maxSize &&
      file.name.length < 120
    ) {
      setfieldErrorFile({ status: false, message: "" });
      setselectedFile(file);
    } else {
      if (!file) {
        setfieldErrorFile({ status: true, message: "Please select a file." });
      } else if (
        file.type !==
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      ) {
        setfieldErrorFile({
          status: true,
          message: "The file format must be Excel.",
        });
        event.target.value = "";
      } else if (file.size >= maxSize) {
        setfieldErrorFile({
          status: true,
          message: "File size should not exceed 100 KB",
        });
        event.target.value = "";
      } else if (file.name.length >= 120) {
        setfieldErrorFile({
          status: true,
          message: "The file name is too long, maximum 120 characters.",
        });
        event.target.value = "";
      }
    }
  };

  const handleImportClick = (selectedFile) => {
    setimported(true);
    if (!selectedFile) {
      setErrorMessage("file", "Select a file before clicking Upload.");
    } else {
      if (selectedBusiness?.id) {
        setfieldError(false);
        setShowProgressModal(true);
        setbusinessError("");
        requestDocumentExcelAccUpload(dispatch, {
          businessID: selectedBusiness.id,
          file: selectedFile,
        });
      } else {
        setbusinessError("Business is required.");
      }
    }
  };

  const renderProgressBar = (label, percentage) => (
    <div className="flex-1 overflow-hidden">
      <div className="w-full">
        <p className="text-xs text-slate-500">{t(label)}</p>
        <div className="w-full mt-1 bg-gray-200 rounded-md sm:rounded-lg dark:bg-gray-700">
          <div
            className="bg-blue-600 text-xs font-medium text-blue-100 text-center p-0.5 leading-none rounded-full"
            style={{ width: `${percentage}%` }}
          >
            {percentage}%
          </div>
        </div>
      </div>
    </div>
  );

  const renderProgressModal = () => (
    <Modal
      title="Excel Document Upload Process"
      show={showProgressModal}
      onClose={() => {
        setShowProgressModal(false);
      }}
      footer={
        <div className="p-4 flex flex-row-reverse gap-2 justify-between items-center border-t border-gray-300 bg-gray-50 rounded-b-lg">
          {uploadLoading
            ? storageRequest !== null
              ? renderProgressBar(
                  "Uploading",
                  Math.round(
                    (progressUpload?.transferredBytes /
                      progressUpload?.totalBytes) *
                      100
                  ),
                  100
                )
              : renderProgressBar("Uploading Excel Document File", 100)
            : null}
        </div>
      }
    >
      <div className="bg-white p-4">
        <div className="flex flex-col justify-center items-center">
          <DocumentIcon className="h-24 w-24 text-slate-500" />
          <div className="w-full text-center">
            <p className="text-xs text-slate-500 truncate">
              {selectedFile?.name}
            </p>
          </div>
        </div>
      </div>
    </Modal>
  );

  const onDownloadProgress = (progress) => {
    const { transferredBytes, totalBytes } = progress;
    if (!totalBytes) {
      setdownloadProgress(0);
    } else {
      setdownloadProgress(transferredBytes / totalBytes);
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "download";
    const clickHandler = () => {
      setTimeout(() => {
        URL.revokeObjectURL(url);
        a.removeEventListener("click", clickHandler);
      }, 150);
    };
    a.addEventListener("click", clickHandler, false);
    a.click();
    return a;
  };

  const downloadFile = async (key) => {
    try {
      setdownloading(true);

      const request = downloadData({
        path: `public/${key}`,
        options: {
          onProgress: onDownloadProgress,
        },
      });

      downloadRef.current = request;
      const result = await request.result;

      const fileName = getAccountingFileName(key);

      downloadBlob(result.body, fileName);
    } catch (downloadError) {
      if (downloadError.message !== "canceled") {
        await handleDownloadError(key, downloadError);
      }
    } finally {
      setdownloading(false);
      setdownloadProgress(0);
    }
  };

  const handleDownloadError = async (key) => {
    try {
      const urlResult = await getUrl({
        path: `public/${key}`,
        options: {
          useAccelerateEndpoint: true,
        },
      });
      const signedURL = urlResult.url.href;
      window.open(signedURL);
    } catch (signedURLError) {
      setdownloadDocError(signedURLError);
    }
  };

  const renderFileModal = () => (
    <Modal
      title="Download File"
      show={downloading}
      onClose={() => {
        if (downloadRef.current) {
          const downloadTask = downloadRef.current;
          downloadTask.cancel();
        }
        setdownloading(false);
      }}
      footer={
        <div className="p-4 flex flex-row-reverse gap-2 justify-between items-center border-t border-gray-300 bg-gray-50 rounded-b-lg">
          <div className="flex-1 overflow-hidden">
            <div className="w-full">
              <p className="text-xs text-slate-500">{t("Downloading")}</p>
              <div className="w-full mt-1 bg-gray-200 rounded-md sm:rounded-lg dark:bg-gray-700">
                <div
                  className="bg-blue-600 text-xs font-medium text-blue-100 text-center p-0.5 leading-none rounded-full"
                  style={{
                    width: `${Math.round(downloadProgress * 100)}%`,
                  }}
                >
                  {Math.round(downloadProgress * 100)}%
                </div>
              </div>
            </div>
          </div>
        </div>
      }
    >
      <div className="bg-white p-4">
        <div className="flex justify-center">
          <DocumentIcon className="h-24 w-24 text-slate-500" />
        </div>
      </div>
    </Modal>
  );

  const renderDownloadError = () => {
    if (downloadDocError) {
      return (
        <ModalConfirm
          open={showDownloadErrorModal}
          type="ALERT"
          setOpen={(open) => {
            setshowDownloadErrorModal(open);
          }}
          title="Error"
          description={getErrorMessages(downloadDocError)}
          onConfirm={() => {
            setdownloadDocError(null);
          }}
        />
      );
    }
    return null;
  };

  function handleDragOver(event) {
    event.preventDefault();
  }

  function handleDragLeave(event) {
    event.preventDefault();
  }

  if (businesses.length > 0) {
    return (
      <div className="max-w-4xl grid grid-cols-1 gap-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
        {renderFileModal()}
        {renderDownloadError()}
        <div className="lg:col-start-3 lg:col-span-1 ">
          <div className="rounded-lg shadow-sm bg-white border border-gray-200 divide-y divide-gray-200">
            <div className="flex flex-col gap-4 px-4 py-6 sm:p-6">
              <div className="flex flex-col gap-4">
                <p className="font-medium text-gray-900">
                  {t("Importing Accounting Accounts via Excel")}
                </p>
                <p className="text-sm text-gray-700">
                  {t("Download the")}{" "}
                  <button
                    className="text-blue-600"
                    onClick={() =>
                      downloadFile("template/accounting_account_template.xlsx")
                    }
                  >
                    {t("Excel format")}.{" "}
                  </button>
                  {t(
                    "Modify the Excel file according to your accounting account data and upload it using the form below."
                  )}
                </p>
              </div>
              <div className="flex gap-4">
                <div className="flex-1 overflow-hidden">
                  <div className="flex">
                    <label
                      htmlFor="upload-input"
                      className={classNames(
                        fileError
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                          : "focus:ring-blue-500 focus:border-blue-500 border-gray-300",
                        "items-center whitespace-nowrap px-4 py-2 bg-gray-100 border-y border-l border-gray-300 rounded-l-md cursor-pointer text-sm text-gray-600 hover:bg-gray-200 active:bg-blue-50 focus:outline-none disabled:opacity-25 transition"
                      )}
                      onDragOver={handleDragOver}
                      onDrop={handleFileUpload}
                      onDragLeave={handleDragLeave}
                    >
                      {t("Choose File")}
                      <input
                        type="file"
                        id="upload-input"
                        className="sr-only"
                        onChange={handleFileUpload}
                        accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      />
                    </label>
                    <div
                      className={classNames(
                        fileError
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                          : "focus:ring-blue-500 focus:border-blue-500 border-gray-300",
                        "flex w-full border rounded-r-md px-4 py-2 overflow-hidden"
                      )}
                      onDragOver={handleDragOver}
                      onDrop={handleFileUpload}
                      onDragLeave={handleDragLeave}
                    >
                      <span className="align-middle text-gray-400 text-sm truncate">
                        {selectedFile?.name || t("File not selected")}
                      </span>
                    </div>
                  </div>
                  {fileError && (
                    <div className="mt-1 text-red-600 text-sm">
                      {t(fileError)}
                    </div>
                  )}
                </div>
                <div>
                  <Button
                    onClick={() => handleImportClick(selectedFile)}
                    title={t("Upload")}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 divide-y divide-gray-200 shadow-sm sm:overflow-hidden rounded-md sm:rounded-lg lg:col-start-1 lg:col-span-2">
          <form>
            <div className="space-y-6 px-4 py-6 sm:p-6">
              {selectedBusiness?.id && (
                <BusinessStatusBanner business={selectedBusiness} />
              )}
              {uploadLoading && renderProgressModal()}
              {(accountID || uploadSuccess) && (
                <ModalConfirm
                  open={showSuccessModal}
                  setOpen={(open) => {
                    setshowSuccessModal(open);
                  }}
                  title="Success"
                  description={
                    uploadSuccess
                      ? "Successfully uploaded Excel document."
                      : "Your accounting account has been created."
                  }
                  type="SUCCESS"
                  onConfirm={navigateToAccount}
                />
              )}
              {uploadStatusError && (
                <ModalConfirm
                  open={showErrorModal}
                  type="ALERT"
                  setOpen={(open) => {
                    setShowErrorModal(open);
                  }}
                  title="Error"
                  description={getErrorMessages(uploadError)}
                  onConfirm={() => {
                    setShowErrorModal(false);
                    navigate(`/account/import?id=${selectedBusiness.id}`);
                  }}
                />
              )}
              {fieldError && (
                <Alert
                  title="Incomplete Form"
                  type="warning"
                  message="Please fill out all fields."
                />
              )}
              {(submitError || getBusinessError) && (
                <Alert
                  title="Error"
                  type="error"
                  message={
                    t(getErrorMessages(submitError || getBusinessError)) ||
                    "Error create Transaction."
                  }
                  errors={submitError?.errors}
                />
              )}
              {fieldErrorFile.status && (
                <Alert
                  title="File Error"
                  type="warning"
                  message={fieldErrorFile.message}
                />
              )}
              <div className="grid grid-cols-1 gap-4">
                {submitLoading ? (
                  <InputSkeleton rows={3} />
                ) : (
                  <div className="col-span-6 sm:col-span-3 space-y-6">
                    <div>
                      <FormLabel
                        htmlFor="businessID"
                        label={t("Business Name")}
                        required
                      />
                      <Select
                        className={classNames(
                          businessError
                            ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                            : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                          "mt-1 text-sm"
                        )}
                        classNamePrefix="select2-selection"
                        options={businesses}
                        placeholder={
                          businesses.length === 0
                            ? t("No Business Profile Yet")
                            : t("Select Business Name")
                        }
                        isLoading={businessLoading}
                        isDisabled={businesses.length === 0}
                        value={selectedBusiness}
                        onChange={setSelectedBusiness}
                      />
                      {businessError && (
                        <div className="mt-1 text-red-600 text-sm">
                          {t(businessError)}
                        </div>
                      )}
                    </div>
                    <div>
                      <FormLabel htmlFor="name" label={t("Name")} required />
                      <input
                        type="text"
                        name="name"
                        value={accountName}
                        placeholder={t("Enter Account Name")}
                        onChange={(e) => setAccountName(e.target.value)}
                        className={classNames(
                          nameError
                            ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                            : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                          "mt-1 block w-full text-sm rounded-md shadow-sm"
                        )}
                      />
                      {nameError && (
                        <div className="mt-1 text-red-600 text-sm">
                          {t(nameError)}
                        </div>
                      )}
                    </div>
                    <div>
                      <FormLabel
                        htmlFor="category"
                        label={t("Category")}
                        required
                      />
                      <Select
                        className={classNames(
                          categoryError
                            ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                            : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                          "mt-1 text-sm"
                        )}
                        classNamePrefix="select2-selection"
                        options={accountCategories}
                        value={category}
                        onChange={(selectedOption) => {
                          setCategory(selectedOption);
                          setInitialCode(ACCOUNT_CODE[selectedOption.value]);
                        }}
                        placeholder={t("Select Category")}
                        maxMenuHeight={150}
                      />
                      {categoryError && (
                        <div className="mt-1 text-red-600 text-sm">
                          {t(categoryError)}
                        </div>
                      )}
                    </div>
                    <div>
                      <FormLabel htmlFor="code" label={t("Code")} required />
                      <div className="mt-1 flex rounded-md shadow-sm">
                        <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                          {initialCode}
                        </span>
                        <input
                          type="text"
                          name="code"
                          id="code"
                          value={code}
                          className={classNames(
                            codeError
                              ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                              : "focus:ring-blue-500 focus:border-blue-500 border-gray-300",
                            "flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md text-sm"
                          )}
                          placeholder={t("Enter Account Code")}
                          onChange={(e) => setcode(e.target.value)}
                          maxLength={3}
                        />
                      </div>
                      {codeError && (
                        <div className="mt-1 text-red-600 text-sm">
                          {t(codeError)}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="bg-gray-50 rounded-b-md sm:rounded-b-lg px-4 py-6 text-right sm:px-6">
              <Button
                type="submit"
                className="w-full sm:w-auto"
                title={t("Create Accounting Account")}
                loading={submitLoading || businessLoading}
                disabled={
                  submitLoading ||
                  businesses.length === 0 ||
                  isBusinessWriteBlocked(selectedBusiness)
                }
                onClick={(e) => {
                  e.preventDefault();
                  onSave();
                }}
              />
            </div>
          </form>
        </div>
      </div>
    );
  }
  return <EmptyBusiness loading={businessLoading} />;
};
